import React, {FormEvent, ReactElement, useEffect, useState} from "react";
import {ReactComponent as CloseIcon} from "components/Icons/close.svg";
import {CoverMoodForm} from "components/UIComponents/cover-mood-form/CoverMoodForm";
import {PageTitle} from "components/UIComponents/PageTitle";
import {FormattedMessage} from "react-intl";
import {FormGroup} from "components/UIComponents/FormGroup";
import {useAppDispatch, useAppSelector} from "lib/redux/hooks";
import {createMenuActions, CreateMenuRequest, createMenuSelectors} from "./createMenuSlice";
import {isEmpty} from "ramda";
import {authSelectors} from "../auth/authSlice";
import {useNavigate, useParams} from "react-router";
import {ErrorMessage} from "components/UIComponents/ErrorMessage";
import {Link} from "react-router-dom";
import {menuManagementActions} from "./menumanagement/menuManagementSlice";
import {FormInput} from "../../components/UIComponents/FormInput";
import {PrimaryButtonBig} from "../../components/UIComponents/PrimaryButtonBig";
import {i18nSelectors} from "../i18n/i18nSlice";
import {LanguageDropdown} from "../i18n/LanguageDropdown";
import {Language} from "../i18n/translations";
import { MenuDocument } from "features/editor/MenuDocumentModel";
import { createDocumentTypeHandler } from "features/editor/documentHandlerUtils";
import { LabelTemplateList } from "features/label-editor/LabelTemplateList";
import { LabelTemplate } from "features/label-editor/LabelDocumentModel";

export interface CreateMenuProps {

}

export function CreateMenu(props: CreateMenuProps): ReactElement {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {menuType} = useParams();

    const type = ((menuType&&['wine', 'leaflet', 'label', 'aperitif'].includes(menuType as any)) ? menuType : 'wine') as MenuDocument['type'];

    const language = useAppSelector(createMenuSelectors.language);
    const appLanguage = useAppSelector(i18nSelectors.language);
    const userName = useAppSelector(authSelectors.customer);
    const menuName = useAppSelector(createMenuSelectors.menuName);
    const menuNameError = useAppSelector(createMenuSelectors.menuNameError);
    const createError = useAppSelector(createMenuSelectors.error);
    const loading = useAppSelector(createMenuSelectors.loading);
    
    const successfullyCreatedMenu = useAppSelector(createMenuSelectors.successfullyCreatedMenu);
    const submitDisabled = loading || isEmpty(menuName);

    const setMenuName = (name: string) => dispatch(createMenuActions.setName(name));
    const setLanguage = (language: Language) => dispatch(createMenuActions.setLanguage(language));

    const [selectLabelTemplate, setSelectLabelTemplate] = useState(false);

    useEffect(() => {
        dispatch(createMenuActions.setLanguage(appLanguage));
    }, [appLanguage, dispatch]);

    const doCreate = (args?: CreateMenuRequest['args']) => {
        if (!userName) return;
        dispatch(createMenuActions.createMenu({
            userName, menuName, language, type, args
        }));
        dispatch(menuManagementActions.invalidateData(userName));
    }

    const onLabelTemplateSelect = (template: LabelTemplate) => {
        doCreate({
            template: template
        });
    };

    const create = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (userName == null) {
            console.error("userName is not set");
            return;
        }

        if (isEmpty(menuName.trim())) {
            dispatch(createMenuActions.setNameError("create.name.error.empty"));
            return;
        }

        if (type === 'label') {
            setSelectLabelTemplate(true);
            return;
        }
        doCreate();
    };

    if (successfullyCreatedMenu != null) {
        navigate(`../edit/${successfullyCreatedMenu.id}`);
        dispatch(createMenuActions.reset());
    }

    const lgTranslations = createDocumentTypeHandler(type).getTranslations();

    return (
        selectLabelTemplate
        ? <LabelTemplateList 
            onCancel={()=>setSelectLabelTemplate(false)}
            onTemplateSelect={onLabelTemplateSelect}
            loading={loading}
        />
        : <CoverMoodForm>
            <form className="flex flex-col h-full" onSubmit={create}>
                <div className="text-right">
                    <Link to="..">
                        <CloseIcon className="inline-block -mr-2.5"/>
                    </Link>
                </div>
                <div className="flex-auto flex flex-col gap-6 justify-center">
                    <PageTitle>
                        <FormattedMessage id={lgTranslations.createMenuTitle}/>
                    </PageTitle>
                    {Boolean(createError) && <ErrorMessage><FormattedMessage id="create.error.couldNotCreate" values={{message: createError}}/></ErrorMessage>}
                    <FormGroup
                        groupTitle={<span className="text-base">
                            <FormattedMessage id={lgTranslations.createMenuName}/>
                        </span>}
                        error={menuNameError}
                    >
                        <FormInput
                            autoFocus
                            disabled={loading}
                            value={menuName}
                            onChange={e => setMenuName(e.target.value)}
                            hasError={Boolean(menuNameError)}
                        />
                    </FormGroup>

                    <FormGroup
                        groupTitle={<span className="text-base"><FormattedMessage id="create.language"/></span>}
                        error={menuNameError}
                    >
                        <LanguageDropdown
                            value={language}
                            onChangeValue={setLanguage}
                            buttonClassName="relative py-3 pl-4 pr-2 w-full
                                            text-sm
                                            bg-white rounded border border-gray-1 outline-0
                                            focus:border-focus"
                        />
                    </FormGroup>
                </div>
                <div className="flex-none">
                    <PrimaryButtonBig
                        type="submit"
                        disabled={submitDisabled}>
                        <FormattedMessage id="create.create"/>
                    </PrimaryButtonBig>
                </div>
            </form>
        </CoverMoodForm>
    );
}
